import { useState, useEffect, useRef, useCallback } from 'react'
import { usePreferences } from '../contexts/PreferencesContext'

const AutoRotationManager = ({
    isActive = false,
    onScreenChange,
    onRotationStateChange,
    activityDetectionEnabled = true,
    children
}) => {
    const {
        preferences,
        updateAutoRotation,
        getScreenDuration,
        goToNextScreen,
        goToPreviousScreen
    } = usePreferences()

    const [currentRotationIndex, setCurrentRotationIndex] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const [pauseReason, setPauseReason] = useState(null)
    const [timeRemaining, setTimeRemaining] = useState(0)
    const [activityDetected, setActivityDetected] = useState(false)

    const rotationTimerRef = useRef(null)
    const countdownTimerRef = useRef(null)
    const activityTimeoutRef = useRef(null)
    const pauseTimeoutRef = useRef(null)
    const advanceToNextScreenRef = useRef(null)

    const autoRotation = preferences.screens?.autoRotation
    const rotationScreens = autoRotation?.screenOrder?.filter(screenId => 
        preferences.screens?.screenConfigs?.[screenId]
    ) || []

    // Calculate adaptive timing based on screen complexity and individual durations
    const getAdaptiveInterval = useCallback((screenId) => {
        // First check if screen has individual duration set
        let baseInterval = getScreenDuration(screenId)
        
        if (!autoRotation.adaptiveTiming) {
            return baseInterval
        }

        const screen = preferences.screens?.screenConfigs?.[screenId]
        if (!screen) return baseInterval

        const complexity = screen.cameras.length

        // Adjust timing based on camera count
        if (complexity <= 1) return Math.max(baseInterval * 0.7, 10) // Faster for single camera
        if (complexity <= 4) return baseInterval // Normal for small grids
        if (complexity <= 9) return baseInterval * 1.3 // Slower for medium grids
        return baseInterval * 1.5 // Slowest for large grids
    }, [getScreenDuration, autoRotation?.adaptiveTiming, preferences.screens?.screenConfigs])

    // Activity detection
    const detectActivity = useCallback(() => {
        if (!activityDetectionEnabled || !autoRotation.pauseOnActivity) return

        setActivityDetected(true)
        
        if (activityTimeoutRef.current) {
            clearTimeout(activityTimeoutRef.current)
        }

        // Resume after 30 seconds of no activity
        activityTimeoutRef.current = setTimeout(() => {
            setActivityDetected(false)
        }, 30000)
    }, [activityDetectionEnabled, autoRotation.pauseOnActivity])

    // Pause rotation temporarily
    const pauseRotation = useCallback((reason, duration = 10000) => {
        setIsPaused(true)
        setPauseReason(reason)

        if (pauseTimeoutRef.current) {
            clearTimeout(pauseTimeoutRef.current)
        }

        pauseTimeoutRef.current = setTimeout(() => {
            setIsPaused(false)
            setPauseReason(null)
        }, duration)
    }, [])

    // Start countdown timer
    const startCountdown = useCallback((duration) => {
        setTimeRemaining(duration)
        
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current)
        }

        countdownTimerRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(countdownTimerRef.current)
                    countdownTimerRef.current = null
                    // Auto-advance when countdown reaches 0
                    console.log('AutoRotation: Countdown reached 0, advancing to next screen')
                    if (advanceToNextScreenRef.current) {
                        setTimeout(() => advanceToNextScreenRef.current(), 100)
                    }
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }, [])

    // Advance to next screen
    const advanceToNextScreen = useCallback(() => {
        console.log('AutoRotation: advanceToNextScreen called')
        const nextScreen = goToNextScreen()
        if (!nextScreen) {
            console.warn('AutoRotation: No next screen returned from goToNextScreen')
            return
        }
        
        console.log(`AutoRotation: Switching to screen ${nextScreen.id} (${nextScreen.name})`)
        
        if (onScreenChange) {
            onScreenChange(nextScreen.id)
        }

        // Start countdown for next rotation with adaptive timing
        const interval = getAdaptiveInterval(nextScreen.id)
        console.log(`AutoRotation: Starting countdown for ${interval} seconds`)
        startCountdown(interval)
    }, [goToNextScreen, onScreenChange, getAdaptiveInterval, startCountdown])

    // Update the ref whenever the function changes
    useEffect(() => {
        advanceToNextScreenRef.current = advanceToNextScreen
    }, [advanceToNextScreen])

    // Main rotation logic - simplified to use only countdown timer
    useEffect(() => {
        console.log('AutoRotation: Main effect triggered', {
            isActive,
            autoRotationEnabled: autoRotation.enabled,
            rotationScreensLength: rotationScreens.length,
            rotationScreens: rotationScreens.map(s => ({ id: s.id, name: s.name }))
        })
        
        if (!isActive || !autoRotation.enabled || rotationScreens.length === 0) {
            console.log('AutoRotation: Stopping rotation', { isActive, enabled: autoRotation.enabled, screenCount: rotationScreens.length })
            // Clear any running timers
            if (rotationTimerRef.current) {
                clearTimeout(rotationTimerRef.current)
                rotationTimerRef.current = null
            }
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current)
                countdownTimerRef.current = null
            }
            setTimeRemaining(0)
            return
        }

        // Check if rotation should be paused
        const shouldPause = isPaused || activityDetected

        if (shouldPause) {
            // Pause timers but don't clear countdown display
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current)
                countdownTimerRef.current = null
            }
            return
        }

        // Start rotation if not already running
        if (!countdownTimerRef.current && timeRemaining === 0) {
            const contextIndex = preferences.screens.autoRotation.currentScreenIndex || 0
            const currentScreenId = rotationScreens[contextIndex]
            console.log('AutoRotation: Starting rotation', { 
                contextIndex, 
                currentScreenId, 
                screenName: currentScreenId ? 'Found' : 'Not found',
                rotationScreensCount: rotationScreens.length 
            })
            if (currentScreenId) {
                const interval = getAdaptiveInterval(currentScreenId)
                console.log('AutoRotation: Starting countdown with interval:', interval)
                startCountdown(interval)
            }
        }
    }, [
        isActive,
        autoRotation.enabled,
        rotationScreens,
        preferences.screens.autoRotation.currentScreenIndex,
        isPaused,
        activityDetected,
        getAdaptiveInterval,
        startCountdown
        // Removed timeRemaining to prevent constant recreation
    ])

    // Handle resuming paused timers
    useEffect(() => {
        if (isActive && autoRotation.enabled && !isPaused && !activityDetected && 
            rotationScreens.length > 0 && !countdownTimerRef.current && timeRemaining > 0) {
            // Resume countdown with remaining time
            startCountdown(timeRemaining)
        }
    }, [isPaused, activityDetected, isActive, autoRotation.enabled, rotationScreens.length, timeRemaining, startCountdown])

    // Initialize rotation index when screens change
    useEffect(() => {
        if (rotationScreens.length > 0) {
            const activeScreenIndex = rotationScreens.findIndex(
                screenId => screenId === preferences.screens.activeScreenId
            )
            const targetIndex = activeScreenIndex !== -1 ? activeScreenIndex : 0
            setCurrentRotationIndex(targetIndex)
            
            // Also sync with preferences context
            if (targetIndex !== preferences.screens.autoRotation.currentScreenIndex) {
                updateAutoRotation({ currentScreenIndex: targetIndex })
            }
        }
    }, [rotationScreens, preferences.screens.activeScreenId, preferences.screens.autoRotation.currentScreenIndex, updateAutoRotation])

    // Report rotation state changes
    useEffect(() => {
        if (onRotationStateChange) {
            const contextIndex = preferences.screens.autoRotation.currentScreenIndex || 0
            onRotationStateChange({
                isActive: isActive && autoRotation.enabled,
                isPaused: isPaused || activityDetected,
                pauseReason: activityDetected ? 'activity' : pauseReason,
                timeRemaining,
                currentScreen: rotationScreens[contextIndex],
                totalScreens: rotationScreens.length,
                screenIndex: contextIndex
            })
        }
    }, [
        isActive,
        autoRotation.enabled,
        isPaused,
        activityDetected,
        pauseReason,
        timeRemaining,
        rotationScreens,
        preferences.screens.autoRotation.currentScreenIndex,
        onRotationStateChange
    ])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current)
            if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
            if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
            if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
        }
    }, [])

    // Public methods via ref (if needed)
    const rotationManagerRef = useRef({
        pause: (reason, duration) => pauseRotation(reason, duration),
        resume: () => {
            setIsPaused(false)
            setPauseReason(null)
        },
        skipToNext: () => advanceToNextScreen(),
        skipToPrevious: () => {
            if (rotationScreens.length === 0) return
            const prevIndex = currentRotationIndex === 0 
                ? rotationScreens.length - 1 
                : currentRotationIndex - 1
            const prevScreenId = rotationScreens[prevIndex]
            setCurrentRotationIndex(prevIndex)
            if (onScreenChange) {
                onScreenChange(prevScreenId)
            }
        },
        detectActivity: () => detectActivity(),
        getCurrentScreen: () => rotationScreens[currentRotationIndex],
        getTimeRemaining: () => timeRemaining,
        isRotating: () => isActive && autoRotation.enabled && !isPaused && !activityDetected
    })

    // Keyboard navigation handler
    const handleKeyboardNavigation = useCallback((event) => {
        if (!isActive) return

        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault()
                detectActivity() // Mark as activity
                
                // Clear current timer
                if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current)
                    countdownTimerRef.current = null
                }
                
                const nextScreen = goToNextScreen()
                if (nextScreen && onScreenChange) {
                    onScreenChange(nextScreen.id)
                }
                // Start fresh timer for new screen
                const nextInterval = getAdaptiveInterval(nextScreen.id)
                setTimeout(() => startCountdown(nextInterval), 100)
                break
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault()
                detectActivity() // Mark as activity
                
                // Clear current timer
                if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current)
                    countdownTimerRef.current = null
                }
                
                const prevScreen = goToPreviousScreen()
                if (prevScreen && onScreenChange) {
                    onScreenChange(prevScreen.id)
                }
                // Start fresh timer for new screen
                const prevInterval = getAdaptiveInterval(prevScreen.id)
                setTimeout(() => startCountdown(prevInterval), 100)
                break
            case ' ':
                event.preventDefault()
                updateAutoRotation({ enabled: !autoRotation.enabled })
                break
        }
    }, [isActive, detectActivity, goToNextScreen, goToPreviousScreen, onScreenChange, getAdaptiveInterval, startCountdown, updateAutoRotation, autoRotation.enabled])

    // Activity detection setup
    useEffect(() => {
        const handleUserActivity = () => {
            if (activityDetectionEnabled && autoRotation.pauseOnActivity) {
                detectActivity()
            }
        }

        const handleKeyDown = (event) => {
            handleKeyboardNavigation(event)
        }

        // Mouse/touch events for activity detection
        const events = ['mousedown', 'mousemove', 'scroll', 'touchstart', 'click']
        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, { passive: true })
        })

        // Keyboard events for navigation and activity
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity)
            })
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [activityDetectionEnabled, autoRotation.pauseOnActivity, detectActivity, handleKeyboardNavigation])

    // Render children with rotation context
    if (children) {
        return children({
            isRotating: isActive && autoRotation.enabled && !isPaused && !activityDetected,
            isPaused: isPaused || activityDetected,
            pauseReason: activityDetected ? 'activity' : pauseReason,
            timeRemaining,
            currentScreen: rotationScreens[currentRotationIndex],
            screenIndex: currentRotationIndex,
            totalScreens: rotationScreens.length,
            rotationManager: rotationManagerRef.current
        })
    }

    return null
}

// Hook for using rotation manager
export const useAutoRotation = () => {
    const { preferences, updateAutoRotation } = usePreferences()
    
    return {
        autoRotation: preferences.screens.autoRotation,
        updateAutoRotation,
        isEnabled: preferences.screens.autoRotation.enabled,
        interval: preferences.screens.autoRotation.interval,
        screenOrder: preferences.screens.autoRotation.screenOrder,
        pauseOnActivity: preferences.screens.autoRotation.pauseOnActivity,
        adaptiveTiming: preferences.screens.autoRotation.adaptiveTiming
    }
}

// Higher-order component for auto-rotation
export const withAutoRotation = (WrappedComponent) => {
    return function AutoRotationWrapper(props) {
        return (
            <AutoRotationManager {...props}>
                {(rotationContext) => (
                    <WrappedComponent {...props} rotation={rotationContext} />
                )}
            </AutoRotationManager>
        )
    }
}

export default AutoRotationManager